'use client';

import { useFrame } from '@react-three/fiber';
import { MeshDistortMaterial, Instances, Instance } from '@react-three/drei';
import { MathUtils } from 'three';
import type { ScrollSceneAnimationControls } from '../../ScrollScene';
import { useRef } from 'react';

export type DebugSceneProps = ScrollSceneAnimationControls & {
	scale?: [x: number, y: number, z: number];
	light?: boolean;
	bubbles?: boolean;
};

/**
 * React component that renders a 3D scene with various visual effects.
 *
 * @param {DebugSceneProps} props - The props for the DebugScene component.
 * @param {number[]} [props.scale] - The scale of the main mesh in the scene. Defaults to [1.5, 1.5, 1.5].
 * @param {boolean} [props.light] - Whether lights should be rendered. Defaults to true.
 * @param {boolean} [props.bubbles] - Whether bubbles should be rendered. Defaults to false.
 * @returns {JSX.Element} - The JSX element representing the 3D scene.
 */
export function DebugScene({ scale = [1.5, 1.5, 1.5], light = true, bubbles = false, ...props }: DebugSceneProps): JSX.Element {
	const { scrollSceneAnimationControls } = props;
	const pointLightRef = useRef<any>(null);
	useFrame(state => {
		if (pointLightRef.current) {
			pointLightRef.current.intensity = (state.camera.position.z * state.size.height * state.viewport.aspect) / 2;
		}
	});
	return (
		<group scale={scale}>
			{/* Render lights if light prop is true */}
			{light && (
				<>
					<pointLight ref={pointLightRef} {...{ intensity: 150, position: [-5, 5, 5] }} />
					<ambientLight {...{ intensity: 0.65 }} />
				</>
			)}

			{/* Render a 3D shape (a sphere) if bubbles prop is false */}
			{!bubbles && (
				<mesh
					onClick={() => {
						// console.log('clicked');
						scrollSceneAnimationControls.setAllowAnimation(!scrollSceneAnimationControls.onScrollSceneAnimation.animating);
					}}
				>
					<sphereGeometry {...{ args: [1, 64, 64] }} />
					<MeshDistortMaterial roughness={0.5} color={'hotpink'} speed={2} />
				</mesh>
			)}

			{/* Render bubbles if bubbles prop is true */}
			{bubbles && <Bubbles {...props} />}
		</group>
	);
}

// Array of particle objects with random factors for positioning
const particles = Array.from({ length: 150 }, () => ({
	xFactor: MathUtils.randFloatSpread(15),
	yFactor: MathUtils.randFloatSpread(10),
	zFactor: MathUtils.randFloatSpread(5),
}));

/**
 * React component that renders multiple instances of a sphere as bubbles.
 * @param {number[]} [props.scale] - The scale of the main mesh in the scene. Defaults to [1.5, 1.5, 1.5].
 * @returns {JSX.Element} - The JSX element representing the bubbles.
 */
function Bubbles(props: ScrollSceneAnimationControls): JSX.Element {
	return (
		<Instances limit={particles.length} castShadow receiveShadow position={[0, 2.5, 0]}>
			<sphereGeometry {...{ args: [0.45, 64, 64] }} />
			<meshStandardMaterial {...{ roughness: 0.5 }} />
			{/* Map over the particles array and render a Bubble component for each particle */}
			{particles.map((data, i) => (
				<Bubble key={i} {...data} {...props} />
			))}
		</Instances>
	);
}

/**
 * React component that renders an instance of a sphere as a bubble.
 *
 * @param {Object} props - The props for the Bubble component.
 * @param {number} props.xFactor - The factor for the x-coordinate of the bubble's position.
 * @param {number} props.yFactor - The factor for the y-coordinate of the bubble's position.
 * @param {number} props.zFactor - The factor for the z-coordinate of the bubble's position.
 * @returns {JSX.Element} - The JSX element representing the bubble.
 */
function Bubble({
	xFactor,
	yFactor,
	zFactor,
	scrollSceneAnimationControls,
}: { xFactor: number; yFactor: number; zFactor: number } & ScrollSceneAnimationControls): JSX.Element {
	return (
		<Instance
			position={[xFactor, yFactor, zFactor]}
			color={generateRandomColorHex()}
			onClick={() => {
				// console.log('clicked');
				scrollSceneAnimationControls.setAllowAnimation(!scrollSceneAnimationControls.onScrollSceneAnimation.animating);
			}}
		/>
	);
}

/**
 * Function that generates a random color in hexadecimal format.
 *
 * @returns {string} - The randomly generated color in the format #RRGGBB.
 */
function generateRandomColorHex(): string {
	const r = Math.floor(Math.random() * 256);
	const g = Math.floor(Math.random() * 256);
	const b = Math.floor(Math.random() * 256);
	return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}
